import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import { usePostFreeformUpload } from '../../utils/api-client/api-hooks.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { FreeformDropzone } from './freeform-dropzone.tsx'
import { FreeformRecentRuns } from './freeform-recent-runs.tsx'
import * as styles from './freeform-tab.css.ts'
import { FREEFORM_MAX_FILES, type StagedFile, validateFreeformFile } from './freeform-types.ts'

type FreeformTabProps = {
  organizationId?: string
  initialStagedFiles?: StagedFile[]
}

export const FreeformTab: React.FC<FreeformTabProps> = ({ organizationId, initialStagedFiles }) => {
  const { addToast } = useToastService()
  const navigate = useNavigate()
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>(initialStagedFiles ?? [])
  const [validationError, setValidationError] = useState<string | undefined>()

  const showMutationError = useShowMutationError({ prefix: 'Failed to upload files' })
  const uploadMutation = usePostFreeformUpload({ organizationId, onError: showMutationError })

  const addFiles = (incoming: File[]) => {
    const rejections: string[] = []
    const accepted: StagedFile[] = []
    let remainingSlots = FREEFORM_MAX_FILES - stagedFiles.length

    for (const file of incoming) {
      const reason = validateFreeformFile(file)
      if (reason !== undefined) {
        rejections.push(reason)
        continue
      }
      if (remainingSlots <= 0) {
        rejections.push(`You can stage at most ${FREEFORM_MAX_FILES} files at once.`)
        break
      }
      accepted.push({ id: crypto.randomUUID(), file })
      remainingSlots -= 1
    }

    if (accepted.length > 0) {
      setStagedFiles(curr => [...curr, ...accepted])
    }
    setValidationError(rejections[0])
  }

  const removeFile = (id: string) => {
    setStagedFiles(curr => curr.filter(staged => staged.id !== id))
    setValidationError(undefined)
  }

  const clearAll = () => {
    setStagedFiles([])
    setValidationError(undefined)
  }

  const handleSubmit = () => {
    if (stagedFiles.length === 0) {
      return
    }
    const files = stagedFiles.map(staged => staged.file)
    uploadMutation.mutate(
      { files },
      {
        onSuccess: data => {
          addToast({
            message: (
              <>
                Uploaded {files.length} {pluralize(files.length, 'file', 'files')} for analysis
              </>
            ),
            variant: 'success',
          })
          navigate(Routes.FreeformRunPage.createPath({ sessionId: data.id }))
        },
      }
    )
  }

  const isPending = uploadMutation.isPending
  const hasStagedFiles = stagedFiles.length > 0
  const submitState = isPending ? 'loading' : hasStagedFiles ? 'default' : 'disabled'

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <p className={styles.toolbarSubtitle}>
          Upload pentest reports, scanner exports, or notes. Pixee parses them, associates each finding with a
          repository, and runs its triage and fix pipeline.
        </p>
        <span className={styles.fileCounter}>
          {stagedFiles.length} / {FREEFORM_MAX_FILES} files
        </span>
      </div>

      <div className={styles.listArea}>
        <FreeformDropzone
          stagedFiles={stagedFiles}
          onAddFiles={addFiles}
          onRemoveFile={removeFile}
          isDisabled={isPending}
          errorMessage={validationError}
        />

        <div className={styles.actionsRow}>
          <GhostButton size="small" state={hasStagedFiles && !isPending ? 'default' : 'disabled'} onClick={clearAll}>
            Clear all
          </GhostButton>
          <DefaultButton size="small" state={submitState} onClick={handleSubmit} loadingLabel="Uploading…">
            Submit{' '}
            {stagedFiles.length > 0 ? `${stagedFiles.length} ${pluralize(stagedFiles.length, 'file', 'files')}` : ''}
          </DefaultButton>
        </div>

        <FreeformRecentRuns organizationId={organizationId} />
      </div>
    </div>
  )
}
